import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  RefreshCw, ShoppingCart, Package, Truck, CheckCircle, XCircle, 
  Eye, ChevronLeft, ChevronRight, Clock, CreditCard, Building2,
  Phone, Mail, MapPin, Calendar, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { generateOrderPDF } from '../../utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEMS_PER_PAGE = 15;

const STATUS_CONFIG = {
  new: { label: 'Nouă', color: 'bg-blue-100 text-blue-800', icon: Clock },
  confirmed: { label: 'Confirmată', color: 'bg-cyan-100 text-cyan-800', icon: CheckCircle },
  processing: { label: 'În procesare', color: 'bg-yellow-100 text-yellow-800', icon: Package },
  shipped: { label: 'Expediată', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Livrată', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Anulată', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const PAYMENT_METHODS = {
  card: { label: 'Card Bancar', icon: CreditCard },
  transfer: { label: 'Transfer Bancar', icon: Building2 },
  cash: { label: 'Ramburs', icon: Truck }
};

const OrdersManager = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Eroare la încărcarea comenzilor');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status actualizat cu succes');
      fetchOrders();
      fetchStats();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadOrderPDF = (order) => {
    try {
      const doc = generateOrderPDF(order);
      const dateStr = new Date(order.created_at).toLocaleDateString('ro-RO').replace(/\./g, '-');
      const customerName = order.customer_name.replace(/\s+/g, '_').substring(0, 20);
      const filename = `${order.order_number}-${dateStr}-${customerName}.pdf`;
      doc.save(filename);
      toast.success('PDF descărcat cu succes');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Eroare la generarea PDF-ului');
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Filter orders by status
  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(o => o.status === statusFilter);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-600" />
        <span className="ml-3 text-slate-600">Se încarcă comenzile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Comenzi Magazin
        </h2>
        <Button onClick={() => { fetchOrders(); fetchStats(); }} variant="outline" size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizează
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="bg-gradient-to-br from-slate-500 to-slate-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-slate-200 text-xs">Total</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.total_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-blue-200 text-xs">Noi</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.new_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-cyan-200 text-xs">Confirmate</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.confirmed_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-yellow-200 text-xs">Procesare</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.processing_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-purple-200 text-xs">Expediate</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.shipped_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-green-200 text-xs">Livrate</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.delivered_orders}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-orange-200 text-xs">Venituri</p>
              <p className="text-lg sm:text-xl font-bold">{stats.total_revenue?.toFixed(0)} LEI</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600">Filtrează:</span>
        <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate comenzile</SelectItem>
            <SelectItem value="new">Noi</SelectItem>
            <SelectItem value="confirmed">Confirmate</SelectItem>
            <SelectItem value="processing">În procesare</SelectItem>
            <SelectItem value="shipped">Expediate</SelectItem>
            <SelectItem value="delivered">Livrate</SelectItem>
            <SelectItem value="cancelled">Anulate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-2 sm:p-6">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Nu există comenzi {statusFilter !== 'all' ? `cu statusul "${STATUS_CONFIG[statusFilter]?.label}"` : ''}.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Nr. Comandă</TableHead>
                      <TableHead className="text-xs sm:text-sm">Client</TableHead>
                      <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Telefon</TableHead>
                      <TableHead className="text-xs sm:text-sm">Total</TableHead>
                      <TableHead className="text-xs sm:text-sm">Status</TableHead>
                      <TableHead className="text-xs sm:text-sm hidden md:table-cell">Data</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedOrders.map((order) => {
                      const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
                      return (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-sm font-semibold text-cyan-600">
                            {order.order_number}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="truncate max-w-[120px]">{order.customer_name}</div>
                          </TableCell>
                          <TableCell className="text-sm hidden sm:table-cell">
                            {order.customer_phone}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-orange-600">
                            {order.total?.toFixed(2)} LEI
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig.color} text-xs`}>
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 hidden md:table-cell">
                            {formatDate(order.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewOrderDetails(order)}
                                title="Vezi detalii"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => downloadOrderPDF(order)}
                                title="Descarcă PDF"
                              >
                                <Download className="w-4 h-4 text-cyan-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between border-t pt-4 mt-4 gap-3">
                  <p className="text-xs sm:text-sm text-slate-600">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredOrders.length)} din {filteredOrders.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button
                        key={i + 1}
                        variant={currentPage === i + 1 ? "default" : "outline"}
                        size="sm"
                        className={`w-8 h-8 p-0 ${currentPage === i + 1 ? 'bg-cyan-600' : ''}`}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-cyan-600" />
                Comandă {selectedOrder?.order_number}
              </div>
              {selectedOrder && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadOrderPDF(selectedOrder)}
                  className="text-cyan-600 border-cyan-300 hover:bg-cyan-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descarcă PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Status Update */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Status curent:</p>
                  <Badge className={`${STATUS_CONFIG[selectedOrder.status]?.color} mt-1`}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Schimbă:</span>
                  <Select 
                    value={selectedOrder.status} 
                    onValueChange={(val) => updateOrderStatus(selectedOrder.id, val)}
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4 text-cyan-500" />
                      Informații Client
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p><strong>Nume:</strong> {selectedOrder.customer_name}</p>
                    <p className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <a href={`tel:${selectedOrder.customer_phone}`} className="text-cyan-600 hover:underline">
                        {selectedOrder.customer_phone}
                      </a>
                    </p>
                    <p className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      <a href={`mailto:${selectedOrder.customer_email}`} className="text-cyan-600 hover:underline">
                        {selectedOrder.customer_email}
                      </a>
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-cyan-500" />
                      Adresa Livrare
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    <p>{selectedOrder.shipping_address}</p>
                    <p>{selectedOrder.shipping_city}{selectedOrder.shipping_county ? `, ${selectedOrder.shipping_county}` : ''}</p>
                    {selectedOrder.shipping_postal_code && <p>Cod: {selectedOrder.shipping_postal_code}</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Order Details */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-cyan-500" />
                    Produse Comandate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          {item.image_url && (
                            <img src={item.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-slate-500">{item.quantity} x {item.unit_price?.toFixed(2)} LEI</p>
                          </div>
                        </div>
                        <p className="font-semibold">{item.total_price?.toFixed(2)} LEI</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t mt-4 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{selectedOrder.subtotal?.toFixed(2)} LEI</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Transport:</span>
                      <span>{selectedOrder.shipping_cost > 0 ? `${selectedOrder.shipping_cost.toFixed(2)} LEI` : 'Gratuit'}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span className="text-orange-500">{selectedOrder.total?.toFixed(2)} LEI</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment & Date */}
              <div className="flex justify-between items-center text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  {PAYMENT_METHODS[selectedOrder.payment_method]?.icon && (
                    React.createElement(PAYMENT_METHODS[selectedOrder.payment_method].icon, { className: "w-4 h-4" })
                  )}
                  <span>Plată: {PAYMENT_METHODS[selectedOrder.payment_method]?.label || selectedOrder.payment_method}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(selectedOrder.created_at)}</span>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="p-3 bg-yellow-50 rounded-lg text-sm">
                  <p className="font-medium text-yellow-800">Note:</p>
                  <p className="text-yellow-700">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManager;
